import { useMemo, useRef, useState } from "react"
import { ChevronDown, Loader2 } from "lucide-react"
import { useLazyGetProcurementSuppliersQuery } from "shared/redux/endpoints/procurementItems"
import { Field } from "../../ui"

let idSeq = 0

const normalize = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()

// Type-to-search supplier picker. GET /suppliers?is_active=true is only called
// the first time the field is opened; the value must be one of its rows —
// typed text that doesn't match a pick is discarded on blur.
// `selectedLabel` shows the saved vendor's name before the list is loaded.
const VendorCombobox = ({ label, required, hint, value, selectedLabel, onChange, disabled }) => {
  const [fid] = useState(() => `pc-vendor-${(idSeq += 1)}`)
  const [load, { data: suppliers, isFetching, isError, isUninitialized }] = useLazyGetProcurementSuppliersQuery()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(null) // null = not typing, show the selected name
  const [active, setActive] = useState(0)
  const listRef = useRef(null)

  const selected = (suppliers || []).find((s) => String(s.id) === String(value))
  const displayName = selected?.name || selectedLabel || (value ? `#${value}` : "")

  const matches = useMemo(() => {
    const q = normalize(query)
    const rows = suppliers || []
    if (!q) return rows
    return rows.filter((s) => normalize(s.name).includes(q) || normalize(s.taxCode).includes(q))
  }, [suppliers, query])

  const openList = () => {
    if (disabled) return
    if (isUninitialized || isError) load({ isActive: true }, true)
    setOpen(true)
    setActive(0)
  }

  const close = () => {
    setOpen(false)
    setQuery(null)
  }

  const pick = (s) => {
    onChange?.(s.id)
    close()
  }

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      if (!open) {
        openList()
        return
      }
      const next = Math.max(0, Math.min(matches.length - 1, active + (e.key === "ArrowDown" ? 1 : -1)))
      setActive(next)
      listRef.current?.children[next]?.scrollIntoView({ block: "nearest" })
    } else if (e.key === "Enter" && open) {
      e.preventDefault()
      if (matches[active]) pick(matches[active])
    } else if (e.key === "Escape") {
      close()
    }
  }

  return (
    <Field label={label} required={required} hint={hint} htmlFor={fid}>
      <div style={{ position: "relative" }}>
        <input
          id={fid}
          className="pc-control"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fid}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search supplier by name or tax code…"
          value={query ?? displayName}
          disabled={disabled}
          onFocus={openList}
          onClick={openList}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
            if (!open) openList()
          }}
          onBlur={close}
          onKeyDown={onKeyDown}
          style={{ paddingRight: 32 }}
        />
        <span
          aria-hidden
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--pc-muted)",
            display: "flex",
            pointerEvents: "none",
          }}
        >
          {isFetching ? <Loader2 size={16} className="pc-spin" /> : <ChevronDown size={16} />}
        </span>
        {open && (
          <ul
            id={`${fid}-list`}
            ref={listRef}
            role="listbox"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              maxHeight: 260,
              overflowY: "auto",
              margin: 0,
              padding: 4,
              listStyle: "none",
              background: "var(--pc-surface)",
              border: "1px solid var(--pc-border)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(15,23,42,.12)",
            }}
          >
            {isFetching && !suppliers ? (
              <li style={{ padding: "8px 10px", color: "var(--pc-muted)", fontSize: 13 }}>Loading suppliers…</li>
            ) : isError ? (
              <li style={{ padding: "8px 10px", color: "var(--pc-danger)", fontSize: 13 }}>
                Couldn&apos;t load suppliers. Close and reopen to retry.
              </li>
            ) : matches.length === 0 ? (
              <li style={{ padding: "8px 10px", color: "var(--pc-muted)", fontSize: 13 }}>No matching supplier.</li>
            ) : (
              matches.map((s, i) => (
                <li
                  key={s.id}
                  role="option"
                  aria-selected={String(s.id) === String(value)}
                  // mousedown, not click: fires before the input's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(s)
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13.5,
                    background: i === active ? "var(--pc-surface-2)" : undefined,
                    fontWeight: String(s.id) === String(value) ? 600 : 400,
                    color: "var(--pc-ink)",
                  }}
                >
                  {s.name}
                  {s.taxCode && <span style={{ color: "var(--pc-muted)", fontSize: 12 }}> · {s.taxCode}</span>}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </Field>
  )
}

export default VendorCombobox
